import { Injectable, OnInit } from '@angular/core';
import { gql } from 'apollo-angular';
import { BehaviorSubject, Subject } from 'rxjs';
import { FOOTER_FIELDS, HEADER_FIELDS } from 'src/app/gql';
import { ContentfulService } from './contentful.service';
import { Constants } from 'src/app/config/app.constants';

@Injectable({
  providedIn: 'root'
})
export class HeaderFooterService {

  query = gql`query siteConfiguration($ID: String!, $preview: Boolean! = false){
      siteConfiguration(id: $ID, preview: $preview) {
        defaultHeader {
          ...HEADER_FIELDS
        }
        defaultFooter {
          ...FOOTER_FIELDS
        }
        startDateTermConfigCollection(limit:5){
          items{
            from,
            to,
            term
          }
        }
      }
    }
    ${HEADER_FIELDS}
    ${FOOTER_FIELDS}`

  headerConfiguration = new BehaviorSubject<HeaderData | false | undefined>(undefined);
  footerConfiguration = new Subject<FooterData | false>();
  startDateTermConfiguration = new BehaviorSubject<StartDateTermData[] | false>(false);
  defaultHeaderData: HeaderData = {} as HeaderData;
  defaultFooterData: FooterData = {} as FooterData;
  defaultStartDateTermData: StartDateTermData[] = [];
  
  // Track if default configuration has been loaded
  public defaultConfigLoaded = false;
  public pendingHeaderUpdate = false;

  constructor(
    private contentful: ContentfulService,
    private constants: Constants,
  ) {}

  /**
   * To get the default configuration for header and footer form contentful
   */
  getDefaultSiteConfiguration() {
    this.contentful.getPageData(this.query, { ID: this.constants.DefaultSiteConfigEntry}).subscribe(res => {
      try {
        // Mark default config as loaded
        this.defaultConfigLoaded = true;
        
        this.defaultHeaderData = res.data.siteConfiguration.defaultHeader as HeaderData;
        this.defaultFooterData = res.data.siteConfiguration.defaultFooter as FooterData;
        this.defaultStartDateTermData = res.data.siteConfiguration.startDateTermConfigCollection.items.length == 0 ? this.constants.defaultStartDateTermConfig : res.data.siteConfiguration.startDateTermConfigCollection.items;
        
        // Process any pending header updates (footer doesn't need race condition handling)
        if (this.pendingHeaderUpdate) {
          this.headerConfiguration.next(this.defaultHeaderData);
          this.pendingHeaderUpdate = false;
        }
        // Footer can be sent immediately (no flash effect concern)
        this.footerConfiguration.next(this.defaultFooterData);
        this.startDateTermConfiguration.next(this.defaultStartDateTermData);
      } catch (e) {
        console.error("Error while extracting data from default site configuration content", e);
      }
      
    })
  }

  getHeaderAndFooterOverride(slug: string) {
    const query = gql`
      query optimizedCombo($slug: String!, $preview: Boolean! = false) {
      optimizedComboCollection(where: { url: $slug }, preview: $preview, limit: 1) {
        items {
          overrideHeader {
            ...on WebsiteHeader {
              ...HEADER_FIELDS
            }
          }
          overrideFooter {
            ...on WebsiteFooter {
              ...FOOTER_FIELDS
            }
          }
        }
      }
    }
    ${HEADER_FIELDS}
    ${FOOTER_FIELDS}`
    this.contentful.getPageData(query, {slug: slug}).subscribe(res => {
      const pageData = res.data.optimizedComboCollection.items[0];
      const header = pageData.overrideHeader ? pageData.overrideHeader : false;
      this.headerConfiguration.next(header);
      // check for footer override
      const footer = pageData.overrideFooter ? pageData.overrideFooter : false;
      this.footerConfiguration.next(footer);
    })
  }
}

export interface FooterData {
  copyright: any,
  linksCollection: any,
  socialLinksCollection : any,
  buttonLink1: ButtonLink,
  buttonLink2: ButtonLink,
  footerLogo: any,
  tagLine: any,
  cookieLink: boolean
  columnLinksCollection: any,
}

export interface HeaderData {
  sys?: { id: string },
  logo: any,
  logoLink: string,
  logoImageUrl: string,
  contactUsPhoneNumber: string,
  phoneNumberLink: string,
  contactUsChat: string,
  contactUsLinkTitle: string,
  contactUsLink: any,
  buttonLink1: ButtonLink,
  buttonLink2: ButtonLink,
  navigationCollection: any,
  mostCommonSearch: string[],
  search: boolean,
  stdLoginTxt: string,
  stdLoginLink: string,
  hidePartnerLogo: boolean,
  isPzd?: boolean
}

export interface StartDateTermData {
  from: string,
  to: string,
  term: string
}

interface ButtonLink {
  docLink: any,
  externalLink: string,
  internalLink: InternalLink | undefined,
  linkTarget: string,
  linkTitle: string,
  jumpTarget: string,
}

interface InternalLink {
  url :string
}
